import type Invoices from "niteroi/packages/sales-distribution/controllers/Invoices.controller";
import {
    DeliveryStatus, ICodes, InvoiceStatus, TQuotationStatus, TSalesOrderStatus, CreditNoteStatus
} from "niteroi/packages/sales-distribution/models/interfaces";
import NumberFormat from "sap/ui/core/format/NumberFormat";
import type BusinessPartners from "niteroi/packages/sales-distribution/controllers/BusinessPartners.controller";

const formatter = {
    formatInvoiceLineTotal(
        this: Invoices,
        quantity: number | string | null | undefined,
        unitPrice: number | string | null | undefined,
        taxCode: number | string | null | undefined,
        currency?: string | null
    ): string {
        const codes = this.getModel("salesDistributionCodes").getData() as ICodes;
        const currencyFormatter = NumberFormat.getCurrencyInstance({
            showMeasure: false // Set to true to include the symbol/code (e.g., € or EUR)
        });

        if (taxCode !== null && unitPrice !== null && quantity !== null && codes.taxCodes.length > 0) {
            const taxItem = codes.taxCodes.find((taxItem) => taxItem.code === taxCode);

            if (taxItem) {
                const itemNetValue = Number(unitPrice) * Number(quantity);
                const itemTaxValue = itemNetValue * (taxItem.amount / 100)
                const itemGrossValue = itemNetValue + itemTaxValue;

                // Format the value using the provided currency or default to "EUR"
                return currencyFormatter.format(itemGrossValue, currency || "EUR");
            }
        }
        return currencyFormatter.format("0", currency || "EUR");
    },

    formatTaxCode(taxCode: string, taxAmount: number, taxDescription: string): string {
        return `${taxCode} - ${taxAmount}% - ${taxDescription}`;
    },

    formatDocumentStatusIcon(status:  InvoiceStatus | DeliveryStatus | TSalesOrderStatus | TQuotationStatus | CreditNoteStatus | null): string {
        let icon;
        switch (status) {
            case "PAID":
            case "COMPLETED":
            case "DELIVERED":
            case "CLOSED":
            case "ACCEPTED":
                icon = "sap-icon://status-completed";
                break;
            case "SHIPPED":
            case "POSTED":
            case "IN_PROCESS":
            case "SENT":
                icon = "sap-icon://status-in-process";
                break;
            case "DRAFT":
            case "OPEN":
                icon = "sap-icon://pending";
                break;
            case "EXPIRED":
                icon = "sap-icon://status-inactive";
                break;
            case "CANCELLED":
            case "REJECTED":
                icon = "sap-icon://status-negative";
                break;
            default:
                icon = "";
        }
        return icon;

    },

    formatDocumentStatusState(status: InvoiceStatus | DeliveryStatus | TSalesOrderStatus | TQuotationStatus | CreditNoteStatus | null): string {
        let state;
        switch (status) {
            case "PAID":
            case "COMPLETED":
            case "DELIVERED":
            case "CLOSED":
            case "ACCEPTED":
                state = "Success";
                break;
            case "SHIPPED":
            case "POSTED":
            case "IN_PROCESS":
            case "SENT":
                state = "Information";
                break;
            case "DRAFT":
            case "OPEN":
                state = "Warning";
                break;
            case "EXPIRED":
                state = "None";
                break;
            case "CANCELLED":
            case "REJECTED":
                state = "Error";
                break;
            default:
                state = "None";
        }
        return state;
    },

    formatBusinessPartnerRole(this: BusinessPartners, partnerRole: string): string {
        switch (partnerRole) {
            case "CUSTOMER":
                return this.getText("salesDistributionBusinessPartnersRoleCustomer");
            case "SUPPLIER":
                return this.getText("salesDistributionBusinessPartnersRoleSupplier");
            case "PROSPECT":
                return this.getText("salesDistributionBusinessPartnersRoleProspect");
            default:
                return partnerRole;
        }
    }
};

export default formatter;

